import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
} from '@angular/core';
import {
    FeatureFlag,
    ITransaction,
} from '@models';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import {
    BulkAddItemsModalComponent,
    TransactionChartModalComponent,
} from '@pages/queues/modals';
import {
    ModalService,
    QueueDefinitionService,
    TransactionService,
} from '@services';
import { encodeParams } from '@utils/encode.utils';

import cloneDeep from 'lodash-es/cloneDeep';
import { TriggerType } from 'main/models/trigger.model';
import {
    forkJoin,
    of,
} from 'rxjs';
import { catchError } from 'rxjs/operators';

const HAS_TRIGGER_LIST = () =>
    cloneDeep([{
        value: [{
            property: 'triggerId',
            method: 'eq',
            value: null,
        }],
        label: 'No',
        translationKey: 'ToggleBoolean_No',
    }, {
        value: [{
            property: 'triggerId',
            method: 'ne',
            value: null,
        }],
        label: 'Yes',
        translationKey: 'ToggleBoolean_Yes',
    }]);

@Component({
    templateUrl: './queues.page.html',
    styleUrls: ['./queues.page.scss'],
    providers: [
        QueueDefinitionService, {
            provide: GRID_SERVICE,
            useClass: TransactionService,
        },
        {
            provide: STATE_HANDLER,
            useValue: urlStateHandler,
        }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class QueuesPageComponent extends ODataGridComponent<ITransaction, number> {
    public isMonitoringEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.Monitoring);

    public hasTriggerList = HAS_TRIGGER_LIST();

    public isCurrentFolderClassic$ = this._appState.user.folder.isClassic$;

    private _modalService = this._injector.get<ModalService>(ModalService);
    private _queueService = this._injector.get<QueueDefinitionService>(QueueDefinitionService);

    public getQueueTriggerParams({ id, name }: ITransaction) {
        return {
            type: TriggerType.Queue,
            queue: encodeParams({ id, name }),
        };
    }

    public removeItems(items: ITransaction[] = this._grid.selectionManager.selected) {
        const hasItemsWithSchedule = !!items.find(i => !!i.triggerId);
        this._modalService.onConfirm(
            null,
            () => {
                forkJoin(
                    items.map(({ id }) =>
                        this._queueService
                            .delete(id)
                            .pipe(catchError(() => of(true))),
                    ),
                )
                    .subscribe(() => this._refresh$.next());
            },
            () => { },
            hasItemsWithSchedule ?
                this._translate.instant('grid_action_delete_queues_with_schedule') :
                this._translate.instant('grid_action_delete_message'),
        );
    }

    public viewChart(entry: ITransaction) {
        const clone = !!entry ? cloneDeep(entry) : undefined;

        this._modalService.openModal(TransactionChartModalComponent, clone, { autoFocus: false, size: 'lg' });
    }

    public onBulkAdd(entry: ITransaction) {
        if (!entry) { return; }

        this._modalService
            .openModal(
                BulkAddItemsModalComponent,
                cloneDeep(entry),
            );
    }
}
